import { useState } from "react";
import { InlineMessage, errorText } from "../ui/Feedback.jsx";
import { useApiClient } from "../api/ApiContext.jsx";
import { useApi } from "../hooks/useApi.js";

/**
 * AlertThresholds
 *
 * The failure-rate thresholds for one project: what is configured, and a form to
 * add or change one.
 *
 * **A rule names a scope, not a calendar or a supplier.** "Each provider" is
 * judged once for every provider the project polls and "each calendar" once for
 * every calendar, and the alert that arrives names which one tripped it. Naming an
 * entity in the rule was the first version of this screen and it made the common
 * case unusable: a customer with forty calendars at one supplier who wanted to hear
 * about any of them failing had to write forty rules to say one thing.
 *
 * **One rule at a time, as a list.** A rule is (project, scope), so setting one
 * scope replaces that scope's rule and leaves the others alone. The API is an
 * upsert per scope for the same reason: a form that submitted the whole set would
 * make every screen that touches one row a way to wipe the rest — the same
 * reasoning that made the API an upsert per scope.
 *
 * **Why the server's limits are rendered rather than enforced here.** The window
 * range is a property of the storage — the counters are hourly, so a shorter window
 * cannot be answered however the form is drawn — and the sample range is a property
 * of the cadence. The panel asks the server for the bounds and uses them, so the
 * day either changes the form follows without anyone editing two numbers in two
 * languages.
 *
 * **Why a rejected rule shows the server's sentence.** The validator knows which
 * rule is impossible and why — a warning above an error is a rule whose two levels
 * can never both be true — and it fails silently for whoever set it that way. The
 * form's job is to put that sentence in front of them, not to invent its own.
 */

const SCOPE_LABELS = {
  project: "The whole project's polling",
  provider: "Each provider separately",
  feed: "Each calendar separately",
};

const SCOPE_HINTS = {
  project: "One rule over every request the project makes.",
  provider: "One rule, judged for every provider: an alert names the supplier that failed.",
  feed: "One rule, judged for every calendar: an alert names the calendar that failed.",
};

/** describeRule names a rule the way a person would. */
function describeRule(rule) {
  const scope = SCOPE_LABELS[rule.scope] ?? rule.scope;
  return `${scope} · last ${rule.windowHours} h · at least ${rule.minPolls} requests`;
}

/** thresholdsOf describes the levels in words, since a bar cannot show two. */
function thresholdsOf(rule) {
  const parts = [];
  if (rule.warningPercent > 0) parts.push(`${rule.warningPercent}% warns`);
  if (rule.errorPercent > 0) parts.push(`${rule.errorPercent}% errors`);
  return parts.length > 0 ? parts.join(" · ") : "No levels set — this rule cannot fire";
}

/** emptyRule is what the form starts as, from the server's defaults. */
function emptyRule(defaults) {
  return {
    scope: "project",
    windowHours: defaults?.windowHours ?? 6,
    warningPercent: defaults?.warningPercent ?? 0,
    errorPercent: defaults?.errorPercent ?? 0,
    minPolls: defaults?.minPolls ?? 5,
    enabled: defaults?.enabled !== false,
  };
}

/** Slider is the shared shape of the two numeric settings. */
function Slider({ label, value, min, max, step = 1, hint, format, onChange }) {
  return (
    <label style={{ display: "grid", gap: 4, fontSize: "var(--text-sm)" }}>
      <span style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span style={{ color: "var(--text-secondary)" }}>{label}</span>
        <span style={{ fontWeight: 500 }}>{format ? format(value) : value}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(event) => onChange(Number(event.target.value))}
        style={{ width: "100%" }}
      />
      <span style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}>
        {hint ?? `${min}–${max}`}
      </span>
    </label>
  );
}

export default function AlertThresholds({ projectId, onSaved }) {
  const api = useApiClient();
  const settings = useApi(() => api.projects.alertRules(projectId), [api, projectId]);

  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const rules = settings.data?.rules ?? [];
  const limits = settings.data?.limits;
  const defaults = settings.data?.defaults;

  function startNew() {
    setMessage(null);
    setDraft(emptyRule(defaults));
  }

  function startEdit(rule) {
    setMessage(null);
    setDraft({ ...rule });
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setMessage(null);
    try {
      await api.projects.saveAlertRule(projectId, draft);
      settings.reload();
      onSaved?.();
      setDraft(null);
      setMessage({ tone: "success", text: "Threshold saved." });
    } catch (error) {
      // The server's sentence, not ours: it knows which rule is impossible.
      setMessage({ tone: "danger", text: errorText(error, "That threshold could not be saved.") });
    } finally {
      setBusy(false);
    }
  }

  async function remove(rule) {
    setBusy(true);
    setMessage(null);
    try {
      await api.projects.deleteAlertRule(projectId, { scope: rule.scope });
      settings.reload();
      onSaved?.();
    } catch (error) {
      setMessage({ tone: "danger", text: errorText(error, "That threshold could not be removed.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 20 }}>
      <p style={{ fontWeight: 500, fontSize: "var(--text-md)", margin: "0 0 2px" }}>
        Failure-rate alerts
      </p>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 12px" }}>
        We call your webhook when the share of failed requests over a window crosses a level, and
        again when it drops back below. A rise is noticed within minutes; a recovery can take up to
        an hour longer, because the failing requests stay in the window until the hour holding them
        passes.
      </p>

      {message ? <InlineMessage tone={message.tone}>{message.text}</InlineMessage> : null}
      {settings.error ? (
        <InlineMessage tone="danger">{errorText(settings.error, "Thresholds could not be loaded.")}</InlineMessage>
      ) : null}

      {settings.loading && !settings.data ? (
        <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>Loading…</p>
      ) : null}

      {!settings.loading || settings.data ? (
        rules.length === 0 ? (
          <p style={{ margin: "0 0 12px", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
            No thresholds are set, so we never alert you about failing polls. The Reliability tab on
            the project shows which calendars and providers are failing.
          </p>
        ) : (
          <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
            {rules.map((rule) => (
              <div
                key={rule.scope}
                data-alert-rule={rule.scope}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
              >
                <span style={{ display: "grid", gap: 2, minWidth: 0 }}>
                  <span style={{ fontSize: "var(--text-sm)" }}>{describeRule(rule)}</span>
                  <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
                    {thresholdsOf(rule)}
                    {rule.enabled ? "" : " · switched off"}
                  </span>
                </span>
                <span style={{ display: "inline-flex", gap: 6, flexShrink: 0 }}>
                  <button
                    type="button"
                    className="ical-btn"
                    style={{ padding: "4px 10px", fontSize: "var(--text-xs)" }}
                    onClick={() => startEdit(rule)}
                    disabled={busy}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="ical-btn"
                    style={{ padding: "4px 10px", fontSize: "var(--text-xs)" }}
                    onClick={() => remove(rule)}
                    disabled={busy}
                  >
                    Remove
                  </button>
                </span>
              </div>
            ))}
          </div>
        )
      ) : null}

      {!draft ? (
        <button type="button" className="ical-btn" onClick={startNew} disabled={busy}>
          + Add threshold
        </button>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          <label style={{ display: "grid", gap: 4, fontSize: "var(--text-sm)" }}>
            <span style={{ color: "var(--text-secondary)" }}>What to watch</span>
            <select
              className="ical-select"
              value={draft.scope}
              aria-label="What to watch"
              onChange={(event) => setDraft({ ...draft, scope: event.target.value })}
            >
              {Object.entries(SCOPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <span style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}>
              {SCOPE_HINTS[draft.scope]}
            </span>
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            <Slider
              label="Window"
              value={draft.windowHours}
              min={limits?.minWindowHours ?? 1}
              max={limits?.maxWindowHours ?? 10}
              hint="how far back the failure share is measured"
              format={(hours) => `${hours} h`}
              onChange={(value) => setDraft({ ...draft, windowHours: value })}
            />
            <Slider
              label="Minimum requests"
              value={draft.minPolls}
              min={limits?.minMinPolls ?? 2}
              max={limits?.maxMinPolls ?? 96}
              hint="below this the window is not judged, so two failed requests cannot raise an alert"
              format={(count) => `${count} requests`}
              onChange={(value) => setDraft({ ...draft, minPolls: value })}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            <Slider
              label="Warning at"
              value={draft.warningPercent}
              min={0}
              max={100}
              hint="0 switches the warning off"
              format={(percent) => (percent > 0 ? `${percent}%` : "off")}
              onChange={(value) => setDraft({ ...draft, warningPercent: value })}
            />
            <Slider
              label="Error at"
              value={draft.errorPercent}
              min={0}
              max={100}
              hint="0 switches the error off"
              format={(percent) => (percent > 0 ? `${percent}%` : "off")}
              onChange={(value) => setDraft({ ...draft, errorPercent: value })}
            />
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "var(--text-sm)" }}>
            <input
              type="checkbox"
              checked={draft.enabled}
              aria-label="Alerts on"
              onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
            />
            Alerts on
          </label>

          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="ical-btn ical-btn-primary" onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save threshold"}
            </button>
            <button type="button" className="ical-btn" onClick={() => setDraft(null)} disabled={busy}>
              Cancel
            </button>
          </div>

          <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
            A rule with no levels set never fires, so a half-filled form cannot start sending
            notifications.
          </p>
        </div>
      )}
    </div>
  );
}
